import {Breakpoints, styled} from '@volkswagen-onehub/components-core';
import {
    ANIMATION_DURATION,
    ANIMATION_FUNCTION,
    createCSSFourValuesPaddingByDirection,
    Direction,
    getStartDirection
} from '../helpers';
import {
    getItemColumns,
    NO_PREVIEW_SLIDE_GAP,
    NO_PREVIEW_START_AND_END_GAP,
    SLIDE_GAP,
    START_AND_END_GAP,
    width
} from './helpers';

export const StyledSimpleCarouselWrapper = styled.div<{
    showDivider: boolean;
}>`
    display: flex;
    flex-direction: column;
    position: relative;
    isolation: isolate;
    ${props =>
        props.showDivider &&
        `border-bottom: 1px solid ${props.theme.colors.border.divider};`}
`;

export const StyledSimpleCarousel = styled.div<{'data-testid': string}>`
    overflow: hidden;
    width: 100%;
    position: relative;
    order: -1;
`;

export const StyledCarouselItemFocusWrap = styled.div`
    position: relative;
    height: 100%;

    &:before {
        content: '';
        border: solid 2px ${props => props.theme.colors.focus.main};
        position: absolute;
        inset: 0;
        z-index: 1;
        pointer-events: none;
        display: none;
    }
`;

type CarouselItemProps = Readonly<{
    isActive: boolean;
    itemCount: number;
    hideNextSlide: boolean;
    isWideSlide: boolean;
    areInactiveSlidesOpaque: boolean;
}>;

export const StyledCarouselItem = styled.div.attrs(
    (props: CarouselItemProps) => ({
        style: {
            opacity:
                props.hideNextSlide ||
                props.isActive ||
                props.areInactiveSlidesOpaque
                    ? 1
                    : 0.5
        }
    })
)<CarouselItemProps>`
    position: relative;
    overflow: hidden;
    width: ${props =>
        width(
            props.itemCount,
            getItemColumns(props.isWideSlide, props.hideNextSlide)
        )}%;
    padding-right: ${props =>
        props.hideNextSlide
            ? width(props.itemCount, NO_PREVIEW_SLIDE_GAP)
            : width(props.itemCount, SLIDE_GAP)}%;
    transition: opacity 0.3s cubic-bezier(0.14, 1.12, 0.67, 0.99);

    &:first-child {
        margin-left: ${props =>
            props.hideNextSlide
                ? width(props.itemCount, NO_PREVIEW_START_AND_END_GAP)
                : width(props.itemCount, START_AND_END_GAP)}%;
    }

    &:focus {
        outline: none;

        ${StyledCarouselItemFocusWrap} {
            &:before {
                display: block;
            }
        }
    }
`;

type StyledCarouselInnerProps = {
    itemCount: number;
    position: number;
    showTransition: boolean;
    maxHeight?: string;
};

export const StyledCarouselInner = styled.div.attrs(
    (props: StyledCarouselInnerProps) => ({
        style: {
            transform: `translate3d(${props.position}%, 0, 0)`,
            transition: `${
                props.showTransition
                    ? `${ANIMATION_DURATION} cubic-bezier(${ANIMATION_FUNCTION})`
                    : ''
            }`
        }
    })
)<StyledCarouselInnerProps>`
    list-style: none;
    display: flex;
    flex-direction: ${props =>
        props.theme.direction !== Direction.RTL ? 'row' : 'row-reverse'};
    margin: 0;
    padding: 0;
    position: relative;
    width: ${props => props.itemCount * 100}%;
    overflow: hidden;
`;

export const StyledCaptionWrapper = styled.div`
    margin-bottom: ${props => props.theme.size.static400};
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 0 ${props => props.theme.size.static150};
`;

export const StyledCaption = styled.div`
    padding: 0 ${props => props.theme.size.grid003};

    @media (min-width: ${Breakpoints.b560}px) {
        padding: 0 ${props => props.theme.size.grid004};
        text-align: center;
    }

    @media (min-width: ${Breakpoints.b960}px) {
        text-align: ${props => getStartDirection(props.theme.direction)};

        padding: ${props =>
            createCSSFourValuesPaddingByDirection(
                0,
                props.theme.size.static150,
                0,
                props.theme.size.grid002,
                props.theme.direction
            )};
    }
`;
